import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useCounty } from "./CountyContext";

const FemaContext = createContext(null);

export function FemaProvider({ children }) {
  const countyContext = useCounty();

  const county =
    countyContext?.county ||
    countyContext?.selectedCounty ||
    countyContext?.currentCounty ||
    "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allDeclarations, setAllDeclarations] = useState([]);

  useEffect(() => {
    let isMounted = true;

    async function loadDeclarations() {
      try {
        setLoading(true);

        const response = await fetch(
          "/disasterDeclarationsSummaries.json"
        );

        if (!response.ok) {
          throw new Error(
            `Failed to load FEMA declarations (${response.status})`
          );
        }

        const data = await response.json();

        if (isMounted) {
          setAllDeclarations(
            data?.DisasterDeclarationsSummaries || []
          );
        }
      } catch (err) {
        console.error("FEMA Load Error:", err);

        if (isMounted) {
          setError(err.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadDeclarations();

    return () => {
      isMounted = false;
    };
  }, []);

  const countyDeclarations = useMemo(() => {
    if (!county) return [];

    return allDeclarations.filter((d) => {
      const designatedCounty = d.designatedArea
        ?.replace(" (County)", "")
        ?.trim();

      return designatedCounty === county;
    });
  }, [county, allDeclarations]);

  const value = useMemo(() => {
    const hasDeclaration =
      countyDeclarations.length > 0;

    const hasIndividualAssistance =
      countyDeclarations.some(
        (d) =>
          d.ihProgramDeclared === true ||
          d.iaProgramDeclared === true
      );

    const hasPublicAssistance =
      countyDeclarations.some(
        (d) => d.paProgramDeclared === true
      );

    const hasHazardMitigation =
      countyDeclarations.some(
        (d) => d.hmProgramDeclared === true
      );

    // Human-friendly aid flags
    const hasResidentAid =
      hasIndividualAssistance;

    const hasGovernmentAid =
      hasPublicAssistance;

    const hasMitigationAid =
      hasHazardMitigation;

    const declarationNumbers = [
      ...new Set(
        countyDeclarations.map(
          (d) => d.femaDeclarationString
        )
      ),
    ];

    const declarationTypes = [
      ...new Set(
        countyDeclarations.map(
          (d) => d.declarationType
        )
      ),
    ];

    const incidentTypes = [
      ...new Set(
        countyDeclarations.map(
          (d) => d.incidentType
        )
      ),
    ];

    const declarationTitles = [
      ...new Set(
        countyDeclarations.map(
          (d) => d.declarationTitle
        )
      ),
    ];

    const declarationsByNumber =
      declarationNumbers.map((number) => ({
        declarationNumber: number,
        records: countyDeclarations.filter(
          (d) =>
            d.femaDeclarationString === number
        ),
      }));

    const latestDeclaration =
      [...countyDeclarations]
        .sort(
          (a, b) =>
            new Date(b.declarationDate) -
            new Date(a.declarationDate)
        )[0] || null;

    return {
      loading,
      error,

      county,

      // Raw FEMA data
      allDeclarations,
      countyDeclarations,

      // Summary counts
      declarationCount:
        countyDeclarations.length,

      // Reference lists
      declarationNumbers,
      declarationTypes,
      incidentTypes,
      declarationTitles,
      declarationsByNumber,

      // Latest matching declaration
      latestDeclaration,

      // FEMA program flags
      hasDeclaration,
      hasIndividualAssistance,
      hasPublicAssistance,
      hasHazardMitigation,

      // Recommended consumer flags
      hasResidentAid,
      hasGovernmentAid,
      hasMitigationAid,
    };
  }, [
    county,
    loading,
    error,
    allDeclarations,
    countyDeclarations,
  ]);

  return (
    <FemaContext.Provider value={value}>
      {children}
    </FemaContext.Provider>
  );
}

export function useFema() {
  const context = useContext(FemaContext);

  if (!context) {
    throw new Error(
      "useFema must be used inside a FemaProvider"
    );
  }

  return context;
}